import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, {
  setAccessToken,
} from "../api";
import { afTrack } from "../analytics/appsflyer";

function friendlyError(e) {
  return (
    e?.response?.data?.detail ||
    e?.response?.data?.message ||
    e?.message ||
    "Something went wrong."
  );
}

export default function PartnerClaim({ setToken, fetchUserInfo }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const claimToken = params.get("token") || "";

  const [status, setStatus] = React.useState("idle"); // idle | confirming | success | error
  const [message, setMessage] = React.useState("");
  const didConfirmRef = React.useRef(false);

  React.useEffect(() => {
    if (!claimToken) {
        setStatus("error");
        setMessage("This claim link is missing a token.");
        return;
    }

    if (didConfirmRef.current) return;
    didConfirmRef.current = true;

    let cancelled = false;

    async function confirmClaim() {
      setStatus("confirming");
      setMessage("");

      try {
        const { data } = await api.post("/partner-claims/confirm", {
          token: claimToken,
        });

        const accessToken = data?.access_token;

        if (accessToken) {
        setAccessToken(accessToken);

        if (typeof setToken === "function") {
            setToken(accessToken);
        }

        if (typeof fetchUserInfo === "function") {
            try {
            await fetchUserInfo(accessToken);
            } catch {}
        }
        }

        if (cancelled) return;

        setStatus("success");
        setMessage(data?.message || "Your partner Smart Timing history is linked.");
        afTrack("partner_claim_success", { entry: "magic_link" });

        navigate("/partner", { replace: true });
      } catch (e) {
        if (cancelled) return;

        const msg = friendlyError(e);
        setStatus("error");
        setMessage(msg);
        afTrack("partner_claim_failed", {
          entry: "magic_link",
          error: msg,
        });
      }
    }

    confirmClaim();

    return () => {
      cancelled = true;
    };
  }, [claimToken]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md ss-card ss-card-pad text-center">
        {status === "confirming" && (
          <>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full border border-orange-500/40 flex items-center justify-center">
              <span className="animate-spin">⏳</span>
            </div>
            <h1 className="text-xl font-bold text-white mb-2">
              Linking your history
            </h1>
            <p className="text-sm text-textMuted">
              Confirming your Smart Timing history link.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
              <span className="text-emerald-400 text-xl">✓</span>
            </div>
            <h1 className="text-xl font-bold text-white mb-2">
              History linked
            </h1>
            <p className="text-sm text-textMuted mb-5">
              {message}
            </p>
            <Link to="/partner" className="ss-btn-primary inline-flex px-5 py-2.5">
                View Partner Dashboard
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center">
              <span className="text-red-300 text-xl">!</span>
            </div>
            <h1 className="text-xl font-bold text-white mb-2">
              Could not link history
            </h1>
            <p className="text-sm text-textMuted mb-5">
              {message}
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/login" className="ss-btn-primary inline-flex justify-center px-5 py-2.5">
                Back to Login
              </Link>
              <p className="text-xs text-zinc-500">
                The link may have expired. Request a new link from the same SatStacker account,
                or contact support if you need help.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}